import type {
	MonthlyInstallationCost,
	MonthlySensorCost,
} from "@sitescene/types";

import { useSearchParams } from "react-router";

import { type Column, DataTable } from "@/components/data-table";
import { DetailPanel, KeyValueList } from "@/components/detail-panel";
import { Button } from "@/components/field";
import { StatusMessage } from "@/components/status-message";
import { useOrgSelection } from "@/context/use-org-selection";
import {
	activeCostGroups,
	formatMonth,
	formatShare,
	formatUnitUsd,
	formatUsd,
	formatWindow,
	monthKey,
	shiftMonth,
} from "@/features/costs/format";
import { useMonthlyCosts } from "@/features/costs/use-monthly-costs";

/**
 * VLM spend of the selected organization for one calendar month (Mountain Time), month
 * to date for the current one, broken down by sensor under each
 * installation. The month rides in the URL so a view can be linked.
 */
export const Costs = () => {
	const { selected } = useOrgSelection();
	const [searchParams, setSearchParams] = useSearchParams();
	const currentMonth = monthKey(new Date());
	const requested = searchParams.get("month");
	// A hand-edited or future ?month= falls back to the current month
	const month =
		requested &&
		/^\d{4}-(0[1-9]|1[0-2])$/.test(requested) &&
		requested <= currentMonth
			? requested
			: currentMonth;
	const isCurrentMonth = month === currentMonth;
	const { data, error, isLoading } = useMonthlyCosts(month);

	const goTo = (next: string) =>
		setSearchParams(next === currentMonth ? {} : { month: next });

	return (
		<section>
			<h2 className="text-primary text-base font-semibold">Costs</h2>
			{selected && (
				<p className="text-secondary mt-1 text-sm">{selected.name}</p>
			)}

			<div className="mt-4 flex items-center gap-3">
				<Button
					aria-label="Previous month"
					onClick={() => goTo(shiftMonth(month, -1))}
					type="button"
					variant="secondary"
				>
					←
				</Button>
				<span className="text-primary min-w-40 text-center text-sm font-medium">
					{formatMonth(month)}
				</span>
				<Button
					aria-label="Next month"
					disabled={isCurrentMonth}
					onClick={() => goTo(shiftMonth(month, 1))}
					type="button"
					variant="secondary"
				>
					→
				</Button>
			</div>

			<div className="mt-4 flex flex-col gap-4">
				<StatusMessage
					error={error}
					isLoading={isLoading}
					loading="Loading costs…"
				/>

				{data && (
					<>
						<DetailPanel title={isCurrentMonth ? "Month to date" : "Month"}>
							<KeyValueList
								items={[
									{
										label: "Total spend",
										value: (
											<span className="font-semibold">
												{formatUsd(data.totalCostUsd)}
											</span>
										),
									},
									...activeCostGroups(data.costByGroup).map(
										({ group, label }) => ({
											label,
											value: `${formatUsd(data.costByGroup[group])} (${formatShare(data.costByGroup[group], data.totalCostUsd)})`,
										}),
									),
									{
										label: "Calls",
										value: data.callCount.toLocaleString(),
									},
									...(data.unpricedCallCount > 0
										? [
												{
													label: "Unpriced calls",
													value: `${data.unpricedCallCount.toLocaleString()} (model missing from pricing table, not included in spend)`,
												},
											]
										: []),
									{ label: "Window", value: formatWindow(data.from, data.to) },
								]}
							/>
						</DetailPanel>

						<StatusMessage
							empty={
								isCurrentMonth
									? "No recorded usage this month."
									: "No recorded usage in this month."
							}
							isEmpty={data.installations.length === 0}
						/>

						{data.installations.map((installation) => (
							<InstallationCosts
								groups={activeCostGroups(data.costByGroup)}
								installation={installation}
								key={installation.id}
								totalCostUsd={data.totalCostUsd}
							/>
						))}
					</>
				)}
			</div>
		</section>
	);
};

interface InstallationCostsProps {
	groups: ReturnType<typeof activeCostGroups>;
	installation: MonthlyInstallationCost;
	totalCostUsd: number;
}

const InstallationCosts = ({
	groups,
	installation,
	totalCostUsd,
}: InstallationCostsProps) => {
	const columns: Column<MonthlySensorCost>[] = [
		{
			header: "Sensor",
			key: "sensor",
			render: (row) => (
				<>
					<span className={row.id ? undefined : "text-tertiary"}>
						{row.name}
					</span>
					{row.externalId && (
						<span className="text-tertiary ml-2 font-mono text-xs">
							{row.externalId}
						</span>
					)}
				</>
			),
		},
		{
			className: "text-right",
			header: "Cost",
			key: "cost",
			render: (row) => formatUsd(row.totalCostUsd),
		},
		...groups.map(({ group, label }) => ({
			className: "text-right",
			header: label,
			key: group,
			render: (row: MonthlySensorCost) => formatUsd(row.costByGroup[group]),
		})),
		{
			className: "text-right",
			header: "Share",
			key: "share",
			render: (row) => formatShare(row.totalCostUsd, totalCostUsd),
		},
		{
			className: "text-right",
			header: "Videos",
			key: "videos",
			render: (row) => row.videoCount.toLocaleString(),
		},
		{
			className: "text-right",
			header: "Cost / video",
			key: "costPerVideo",
			render: (row) => formatUnitUsd(row.costPerVideo),
		},
		{
			className: "text-right",
			header: "Calls",
			key: "calls",
			render: (row) => row.callCount.toLocaleString(),
		},
	];

	return (
		<DetailPanel
			title={`${installation.externalId} · ${formatUsd(installation.totalCostUsd)}`}
		>
			<DataTable
				columns={columns}
				rowKey={(row) => row.id ?? "unattributed"}
				rows={installation.sensors}
			/>
		</DetailPanel>
	);
};
